import React from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { OrderStatus } from "@/types/enums/OrderStatus"
import {
  getOrderTerminalStatusLabel,
  getOrderTerminalStatusMessage,
} from "@/domain/order/order-details-stepper"
import { getOrderStatusColor } from "@/utils/order-status"
import { accessibleForegroundOn } from "@/theme/accessible-on-fill"

export type OrderTerminalStatusProps = {
  terminalStatus: OrderStatus
  /** Backend-provided reason message (cancelled orders only); replaces the generic copy when set. */
  reasonMessage?: string | null
}

/**
 * Dedicated UI for terminal non-success order statuses (cancelled, returned, refunded).
 * Does not render workflow progression.
 */
export function OrderTerminalStatus({ terminalStatus, reasonMessage }: OrderTerminalStatusProps) {
  const theme = useTheme()
  const label = getOrderTerminalStatusLabel(terminalStatus)
  const message = reasonMessage?.trim() || getOrderTerminalStatusMessage(terminalStatus)
  const accent = getOrderStatusColor(terminalStatus, theme)

  return (
    <Box
      role="status"
      aria-label={`${label}: ${message}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: theme.spacing(0.375),
        width: "100%",
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-block",
          py: theme.spacing(0.25),
          px: theme.spacing(1),
          borderRadius: "9999px",
          fontWeight: theme.typography.fontWeightBold,
          typography: "caption",
          bgcolor: accent,
          color: accessibleForegroundOn(accent),
          lineHeight: 1.2,
        }}
      >
        {label}
      </Box>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, lineHeight: 1.4 }}>
        {message}
      </Typography>
    </Box>
  )
}
